import React, { useState } from 'react';
import { useData } from "../backend/firebase";
import GoFundMeEmbed from '../components/GoFundMeEmbed';
import PrizePoolBreakdown from '../components/PrizePoolBreakdown';
import Bracket from '../components/Bracket';
import SEO from '../components/SEO';

const Rules = () => {
    const { conditionsText, teams, brackets, donationsContent} = useData();
    const [activeTab, setActiveTab] = useState(0);

    // --- HELPER 1: Extract Color Macro ---
    let rawText = conditionsText || "No conditions posted.";
    
    // Default Colors (Softer slate background, Gold accent)
    let cardBgColor = '#25283B'; 
    let accentColor = '#FFD700';

    // Look for [[TAB_COLORS:#bghex,#accenthex]]
    const colorRegex = /\[\[TAB_COLORS:\s*(#[0-9a-fA-F]{3,8})\s*,\s*(#[0-9a-fA-F]{3,8})\s*\]\]/;
    const colorMatch = rawText.match(colorRegex);
    
    if (colorMatch) {
        cardBgColor = colorMatch[1];
        accentColor = colorMatch[2];
    }

    let pageTitle = null;
    const titleRegexMacro = /\[\[TITLE:(.*?)]\]/;
    const titleMatchMacro = rawText.match(titleRegexMacro);

    if (titleMatchMacro) {
        pageTitle = titleMatchMacro[1];
        // Remove it from rawText so it doesn't render inside the tabs
        rawText = rawText.replace(titleRegexMacro, '');
    }

    // Strip the color macro out of the text so it doesn't render on the screen
    rawText = rawText.replace(colorRegex, '');

    // --- HELPER 2: Parse Tabs ---
    const tabRegex = /(?:<p[^>]*>)?\s*\[\[TAB:(.*?)]\]\s*(?:<\/p>)?/;
    const hasTabs = rawText.includes('[[TAB:');

    let parsedTabs = [];

    if (hasTabs) {
        const parts = rawText.split(tabRegex);
        for (let i = 1; i < parts.length; i += 2) {
            parsedTabs.push({
                title: parts[i].trim(),
                content: parts[i + 1] || ""
            });
        }
    }

    // --- HELPER 3: Render Inner Macros ---
    const renderContent = (contentString) => {
        if (!contentString) return null;

        // const splitRegex = /({{GOFUNDME}}|\[\[BRACKET_MAIN(?:[:0-9.]*)\]\]|\[\[BRACKET_CONSOLATION(?:[:0-9.]*)\]\]|\[\[TITLE:.*?\]\])/;
        // const parts = contentString.split(splitRegex);
        
        const splitRegex = /({{GOFUNDME}}|{{PRIZE_POOL}}|\[\[BRACKET_MAIN(?:[:0-9.]*)\]\]|\[\[BRACKET_CONSOLATION(?:[:0-9.]*)\]\])/;
        const parts = contentString.split(splitRegex);


        return parts.map((part, index) => {
            if (!part) return null;

            // if (part.startsWith('[[TITLE:')) {
            //     const titleText = part.replace('[[TITLE:', '').replace(']]', '');
            //     return <h1 key={index} className="page-header-title">{titleText}</h1>;
            // }

            if (part === '{{GOFUNDME}}') return <GoFundMeEmbed key={index} type="medium" />;

            if (part === '{{PRIZE_POOL}}') return <PrizePoolBreakdown key={index} />;
            
            if (part.startsWith('[[BRACKET_MAIN')) {
                const scaleMatch = part.match(/:([0-9.]+)/);
                const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;
                return (
                    // CHANGED: display to inline-block and verticalAlign to top
                    <div key={index} style={{ display: 'block', zoom: scale, width: 'fit-content', maxWidth: '100%', margin: '30px auto' }}>
                        <Bracket type="main" data={{}} teams={teams} highContrast={true} />
                    </div>
                );
            }

            if (part.startsWith('[[BRACKET_CONSOLATION')) {
                const scaleMatch = part.match(/:([0-9.]+)/);
                const scale = scaleMatch ? parseFloat(scaleMatch[1]) : 1;
                return (
                    // CHANGED: display to inline-block and verticalAlign to top
                    <div key={index} style={{ display: 'block', zoom: scale, width: 'fit-content', maxWidth: '100%', margin: '30px auto' }}>
                        <Bracket type="consolation" data={{}} teams={teams} highContrast={true} />
                    </div>
                );
            }
            
            return <div key={index} dangerouslySetInnerHTML={{ __html: part }} />;
        });
    };

    // --- RENDER FALLBACK (No Tabs) ---
    if (!hasTabs) {
        return (
            <div className="PageLayout">
                <SEO title="Competition Info" description="Read the official rules, bracket structures, and conditions of contest for the Bridge Brawl." url="/rules" />
                <div className="ContentWrapper">
                    <div className="GlassCard rules-content-card">
                        <div className="ScrollArea">
                            {/* ADDED textAlign: 'center' so inline-blocks center naturally */}
                            <div className="RichTextContent" style={{color: '#E0E0E0', lineHeight: '1.6', paddingBottom: 50, textAlign: 'center'}}>
                                {renderContent(rawText)}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const currentTabContent = parsedTabs[activeTab]?.content || "";

    return (
        <div className="PageLayout" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
            <SEO 
                title="Competition Info" 
                description="Read the official rules, bracket structures, and conditions of contest for the Bridge Brawl."
                url="/rules" 
            />
            
            <style>{`
                .rules-tabs-container {
                    display: flex;
                    max-width: 1200px;
                    width: 100%;
                    margin: 0 auto;
                    flex-wrap: wrap; 
                    gap: 6px;
                    padding-left: 24px;
                    scrollbar-width: none; 
                }
                .rules-tabs-container::-webkit-scrollbar { display: none; }

                .rules-tab-btn {
                    background: rgba(30, 34, 45, 0.6); 
                    border: 1px solid rgba(255, 255, 255, 0.05);
                    border-bottom: none;
                    color: #A0AEC0;
                    padding: 12px 24px;
                    border-radius: 10px 10px 0 0; 
                    cursor: pointer;
                    font-weight: 600;
                    font-size: 14px;
                    transition: all 0.2s ease;
                    margin-bottom: -1px; 
                    position: relative;
                    z-index: 1;
                    flex-shrink: 0 !important;
                    white-space: nowrap;
                }

                .rules-tab-btn:hover {
                    background: rgba(45, 50, 65, 0.9);
                    color: #FFFFFF;
                }

                .rules-tab-btn.active {
                    background: ${cardBgColor}; 
                    color: #FFFFFF;
                    border: 1px solid rgba(255, 255, 255, 0.1);
                    border-bottom: 1px solid ${cardBgColor}; 
                    border-top: 3px solid ${accentColor}; 
                    z-index: 3;
                }

                /* THE NEW CARD STYLING */
                .rules-content-card {
                    background: linear-gradient(145deg, ${cardBgColor} 0%, rgba(20, 22, 32, 0.95) 100%) !important; 
                    border-radius: 12px !important; 
                    border: 1px solid rgba(255, 255, 255, 0.08) !important;
                    box-shadow: 0 15px 40px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.05) !important;
                }

                /* TYPOGRAPHY UPGRADES */
                .RichTextContent {
                    text-align: left; /* Much easier to read */
                    color: #D1D5DB; /* Slightly softer than pure white */
                    line-height: 1.7;
                    font-size: 16px;
                }
                
                .RichTextContent p {
                    margin-bottom: 1.5em;
                }

                .RichTextContent h1, .RichTextContent h2, .RichTextContent h3 {
                    color: #FFFFFF;
                    margin-top: 1.5em;
                    margin-bottom: 0.8em;
                    font-weight: 600;
                }

                .page-header-title {
                    display: block;        /* Ensures it takes full width */
                    text-align: center;    /* Centers the text */
                    width: 100%;
                    font-size: 2.0rem;
                    font-weight: 800;
                    color: #FFFFFF;
                    margin: 30px 0 20px 0;
                    text-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
                }

                .RichTextContent h2 {
                    border-bottom: 1px solid rgba(255,255,255,0.08);
                    padding-bottom: 8px;
                }

                @media (max-width: 768px) {
                    .rules-tabs-container {
                        padding: 0 10px;
                        gap: 8px;
                        margin-bottom: 15px;
                        display: flex !important;
                        flex-wrap: nowrap !important;
                        overflow-x: auto !important;
                        min-height: 48px !important;
                        overflow-y: hidden !important;
                    }

                    .rules-tab-btn {
                        padding: 10px 15px !important;
                        font-size: 13px !important;
                        min-width: fit-content !important;
                        border-radius: 8px 8px 0 0;
                        flex: 0 0 auto !important;
                    }
                    .page-header-title {
                        font-size: 1.8rem;
                        margin: 20px 0 10px 0;
                    }
                    
                    .RichTextContent {
                        font-size: 15px; /* Slightly smaller on mobile */
                    }
                }
            `}</style>

            <div className="ContentWrapper CustomScroll" style={{flex: 1, overflowY: 'auto', padding: '15px 10px 100px 10px' }}>
                
                {pageTitle && <h1 className="page-header-title">{pageTitle}</h1>}

                <div className="rules-tabs-container">
                    {parsedTabs.map((tab, index) => (
                        <button 
                            key={index}
                            onClick={() => setActiveTab(index)}
                            className={`rules-tab-btn ${activeTab === index ? 'active' : ''}`}
                        >
                            {tab.title}
                        </button>
                    ))}
                </div>

                <div className="GlassCard rules-content-card" style={{ maxWidth: 1200, margin: '0 auto' }}>
                    {/* Bumbed padding from 20px to 40px for desktop, handles breathing room naturally */}
                    <div style={{ padding: '40px 5%' }}>
                        <div className="RichTextContent">
                            {renderContent(currentTabContent)}
                        </div>
                    </div>
                </div>

            </div>
        </div>
    );
};

export default Rules;